import React, { createContext, useContext, useState, useEffect } from 'react';

interface AdminUser {
  adminId: number;
  username: string;
  fullName: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: AdminUser | null;
  login: (username: string, password: string, userData: AdminUser) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: () => {},
  logout: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('adminUser');
    const credentials = localStorage.getItem('adminCredentials');
    if (stored && credentials) {
      try {
        const userData = JSON.parse(stored);
        const credData = JSON.parse(credentials);
        // Ensure we have valid user data and credentials
        if (userData && userData.adminId && credData && credData.username) {
          setUser(userData);
        } else {
          // Clear invalid data
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminCredentials');
        }
      } catch (error) {
        // Clear corrupted data
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminCredentials');
      }
    }
  }, []);

  const login = (username: string, password: string, userData: AdminUser) => {
    localStorage.setItem('adminCredentials', JSON.stringify({ username, password }));
    localStorage.setItem('adminUser', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('adminCredentials');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
